import React, { useState, useEffect, useCallback } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { backdropClose } from '../backdrop';

// Prompt version viewer / comparer. Pick a prompt (by role/stage), then a
// version on the left and another on the right — the diff is rendered by
// react-diff-viewer-continued (side-by-side, char-level highlight, +/- gutter
// markers). Versions come straight from git history via the backend, so this is
// read-only and never mutates a prompt file.
//
// Colours: added = blue, removed = amber — NOT red/green, and always paired
// with the +/- gutter markers, so meaning never rides on hue alone.

async function api(path) {
  const res = await fetch(`/api/pipeline${path}`, { credentials: 'include' });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

// ISO commit time -> "YYYY-MM-DD HH:MM:SS" (in the commit's own offset).
const fmtStamp = (iso) => (iso ? `${iso.slice(0, 10)} ${iso.slice(11, 19)}` : '');

// One-line label for a version in the dropdowns.
const versionLabel = (v) => {
  if (v.ref === 'worktree') return `● On disk (uncommitted) · ${v.hash || ''}`;
  const ver = v.version != null ? `v${v.version}` : '(no version)';
  return `${ver} · ${fmtStamp(v.date)} · ${v.subject || ''}`;
};

// Blue (added) / amber (removed) palette — colour-blind safe, unlike red/green.
const diffStyles = {
  variables: {
    light: {
      diffViewerBackground: '#fff',
      addedBackground: '#dbeafe', addedColor: '#0b3d91',
      removedBackground: '#ffedd5', removedColor: '#7c3a00',
      wordAddedBackground: '#93c5fd', wordRemovedBackground: '#fdba74',
      addedGutterBackground: '#bfdbfe', removedGutterBackground: '#fed7aa',
      gutterBackground: '#f7f7f7', codeFoldBackground: '#f1f5f9',
    },
    dark: {
      diffViewerBackground: '#1e1e1e',
      addedBackground: '#0b2f5e', addedColor: '#cfe3ff',
      removedBackground: '#4a2c00', removedColor: '#ffe4c4',
      wordAddedBackground: '#1e4d8c', wordRemovedBackground: '#7c4a00',
      addedGutterBackground: '#123a6b', removedGutterBackground: '#5a3600',
      gutterBackground: '#2a2a2a', codeFoldBackground: '#2a2a2a',
    },
  },
  contentText: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 },
  line: { wordBreak: 'break-word' },
};

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border,#ccc)', fontSize: 13, maxWidth: '100%' };

const PromptDiffModal = ({ onClose }) => {
  const [nodes, setNodes] = useState([]);
  const [nodeKey, setNodeKey] = useState('');
  const [versions, setVersions] = useState([]);
  const [leftRef, setLeftRef] = useState('');
  const [rightRef, setRightRef] = useState('');
  const [leftRaw, setLeftRaw] = useState('');
  const [rightRaw, setRightRaw] = useState('');
  const [error, setError] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Load the list of browsable prompts once.
  useEffect(() => {
    (async () => {
      try {
        const d = await api('/prompt-nodes');
        setNodes(d.nodes || []);
        if (d.nodes && d.nodes.length) setNodeKey(d.nodes[0].key);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // When the prompt changes, pull its version list and default to comparing the
  // two newest states (previous -> current).
  useEffect(() => {
    if (!nodeKey) return;
    setLoadingVersions(true); setError(null);
    // Blank the refs too, so a stale ref from the previous prompt is never
    // fetched against the new file (which would 404 as "Version not found").
    setVersions([]); setLeftRaw(''); setRightRaw(''); setLeftRef(''); setRightRef('');
    (async () => {
      try {
        const d = await api(`/prompt-nodes/${encodeURIComponent(nodeKey)}/versions`);
        const vs = d.versions || [];
        setVersions(vs);
        setRightRef(vs[0] ? vs[0].ref : '');
        setLeftRef(vs[1] ? vs[1].ref : (vs[0] ? vs[0].ref : ''));
      } catch (e) { setError(e.message); }
      finally { setLoadingVersions(false); }
    })();
  }, [nodeKey]);

  // Fetch one side's full text whenever its selected ref changes.
  const fetchSide = useCallback(async (ref, setRaw) => {
    if (!ref) { setRaw(''); return; }
    try {
      const d = await api(`/prompt-nodes/${encodeURIComponent(nodeKey)}/version/${encodeURIComponent(ref)}`);
      setRaw(d.raw || '');
      setError(null);
    } catch (e) { setError(e.message); }
  }, [nodeKey]);
  useEffect(() => { fetchSide(leftRef, setLeftRaw); }, [leftRef, fetchSide]);
  useEffect(() => { fetchSide(rightRef, setRightRaw); }, [rightRef, fetchSide]);

  const grouped = nodes.reduce((acc, n) => { (acc[n.group] = acc[n.group] || []).push(n); return acc; }, {});
  const identical = leftRaw && rightRaw && leftRaw === rightRaw;

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000,
    display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 20,
  };
  const panel = {
    background: 'var(--bg,#fff)', color: 'var(--text,#111)', borderRadius: 10, width: '100%', maxWidth: 1280,
    display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };

  return (
    <div style={overlay} {...backdropClose(onClose)}>
      <div className="pl-modal" style={panel} onClick={(e) => e.stopPropagation()}>
        {/* Header + prompt picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border,#eee)', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>🔀 Compare prompt versions</h2>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            Prompt:
            <select value={nodeKey} onChange={(e) => setNodeKey(e.target.value)} style={selectStyle}>
              {Object.keys(grouped).map((g) => (
                <optgroup key={g} label={g}>
                  {grouped[g].map((n) => (
                    <option key={n.key} value={n.key}>{n.label}{n.version != null ? ` (v${n.version})` : ''}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <button onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
        </div>

        {/* Version pickers */}
        <div style={{ display: 'flex', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border,#eee)', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 300px', minWidth: 0, fontSize: 12, opacity: 0.85 }}>
            <div style={{ marginBottom: 4 }}>Left (older / base)</div>
            <select value={leftRef} onChange={(e) => setLeftRef(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              {versions.map((v) => <option key={v.ref} value={v.ref}>{versionLabel(v)}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 300px', minWidth: 0, fontSize: 12, opacity: 0.85 }}>
            <div style={{ marginBottom: 4 }}>Right (newer / compare)</div>
            <select value={rightRef} onChange={(e) => setRightRef(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              {versions.map((v) => <option key={v.ref} value={v.ref}>{versionLabel(v)}</option>)}
            </select>
          </label>
        </div>

        {error && <div style={{ color: '#c5221f', padding: '8px 16px', fontSize: 13 }}>{error}</div>}

        {/* Diff */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 4px 8px' }}>
          {loadingVersions ? (
            <div style={{ padding: 24, opacity: 0.6 }}>Loading versions…</div>
          ) : versions.length === 0 ? (
            <div style={{ padding: 24, opacity: 0.6 }}>No git history for this prompt.</div>
          ) : identical ? (
            <div style={{ padding: 24, opacity: 0.7 }}>
              These two versions are identical.
            </div>
          ) : (
            <ReactDiffViewer
              oldValue={leftRaw}
              newValue={rightRaw}
              splitView
              compareMethod={DiffMethod.WORDS}
              styles={diffStyles}
              leftTitle="Left"
              rightTitle="Right"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptDiffModal;
